import { FC, Fragment, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useRef } from "react";
import html2canvas from "html2canvas";
import useAppSelector from "hooks/useAppSelector";
import { useNavigate, useParams } from "react-router-dom";
import { apiBookingFindById } from "apis";
import NotFound from "layouts/view/component/NotFound";
import AppLoading from "components/AppLoading";
import { QRCodeCanvas } from "qrcode.react";


interface HomeProps { }
const BookingAlert: FC<HomeProps> = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const cardRef = useRef<HTMLDivElement>(null);
    const handleDownload = () => {
        if (!cardRef.current) return;

        cardRef.current.classList.remove("rounded-2xl")
        html2canvas(cardRef.current, {
            scale: 2,
        })
            .then((canvas) => {
                cardRef.current?.classList.add("rounded-2xl")
                const link = document.createElement("a");
                link.download = "phieu-hen.png";
                link.href = canvas.toDataURL("image/png");
                link.click();
            })
            .catch(() => {
                cardRef.current?.classList.add("rounded-2xl")
            });
    };
    const booking = useAppSelector(state => state.booking);

    useEffect(() => {
        id && apiBookingFindById({ id: parseInt(id) });
    }, [id])

    if (booking.loading) {
        return <AppLoading />
    }
    if (!booking.item.data) {
        return <NotFound />;
    }
    return (
        <Fragment>
            <Helmet>
                <title>Dashboard | Bệnh viện</title>
                <meta name="description" content="Thông tin liên hệ với chúng tôi" />
            </Helmet>
            <section className="py-10">
                <div style={{ position: "fixed", top: -1000 }}>
                    <div className="container mx-auto px-4">
                        <div className="max-w-lg mx-auto bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200" ref={cardRef}>
                            {/* Header */}
                            <div className="bg-blue-600 text-white p-6 flex justify-between items-center">
                                <div>
                                    <h1 className="text-sm uppercase tracking-widest opacity-90">
                                        Phiếu hẹn khám
                                    </h1>
                                    <p className="text-5xl font-extrabold mt-1">{booking.item.data.index}</p>
                                    <p className="opacity-80 mt-1">Ngày đặt</p>
                                    <p className="font-semibold">{booking.item.data.createdAt}</p>
                                </div>
                                <div className="text-right text-sm">
                                    <div className="w-[110px] h-[110px] bg-white rounded flex items-center justify-center">
                                        <QRCodeCanvas
                                            value={`/dat-hen/phieu-hen/${booking.item.data.id}.html`}           // Dữ liệu cần mã hóa (text, link, JSON, v.v.)
                                            size={100}             // Kích thước (px)
                                            bgColor="#ffffff"      // Màu nền
                                            fgColor="#000000"      // Màu QR
                                            level="Q"              // Mức độ sửa lỗi (L, M, Q, H)
                                            includeMargin={false}   // Thêm margin xung quanh
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Nội dung */}
                            <div className="p-6 space-y-5">
                                <div className="flex items-center">
                                    <i className="fa-2x fa-solid fa-hospital-user text-blue-500 w-10" />
                                    <div className="ml-3">
                                        <p className="text-sm text-gray-500">Chuyên khoa</p>
                                        <p className="font-semibold">{booking.item.data.specialty?.name || "-"}</p>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <i className="fa-2x fa-solid fa-user-doctor text-blue-500 w-10" />
                                    <div className="ml-3">
                                        <p className="text-sm text-gray-500">Bác sĩ</p>
                                        <p className="font-semibold">{booking.item.data.user ? `${booking.item.data.user.firstName} ${booking.item.data.user.lastName}` : "-"}</p>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <i className="fa-2x fa-solid fa-square-check text-blue-500 w-10" />
                                    <div className="ml-3">
                                        <p className="text-sm text-gray-500">Hình thức</p>
                                        <p className="font-semibold">Lấy vé tại quầy</p>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <i className="fa-2x fa-solid fa-clock text-blue-500 w-10" />
                                    <div className="ml-3">
                                        <p className="text-sm text-gray-500">Thời gian</p>
                                        <p className="font-semibold">{booking.item.data.date} {booking.item.data.timeFrame?.name}</p>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <i className="fa-2x fa-solid fa-info text-blue-500 w-10" />
                                    <div className="ml-3">
                                        <p className="text-sm text-gray-500">Người đặt</p>
                                        <p className="font-semibold">{booking.item.data.fullName}</p>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <i className="fa-2x fa-solid fa-envelope text-blue-500 w-10" />
                                    <div className="ml-3">
                                        <p className="text-sm text-gray-500">Số điện thoại</p>
                                        <p className="font-semibold">{booking.item.data.email || "-"}</p>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <i className="fa-2x fa-solid fa-file-pen text-blue-500 w-10" />
                                    <div className="ml-3">
                                        <p className="text-sm text-gray-500">Mô tả triệu chứng</p>
                                        <p className="font-semibold">{booking.item.data.description || "-"}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container mx-auto px-4">
                    <div className="max-w-lg mx-auto bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200">
                        {/* Header */}
                        <div className="bg-blue-600 text-white p-6 flex justify-between items-center">
                            <div>
                                <h1 className="text-sm uppercase tracking-widest opacity-90">
                                    Phiếu hẹn khám
                                </h1>
                                <p className="text-5xl font-extrabold mt-1">{booking.item.data.index}</p>
                                <p className="opacity-80 mt-1">Ngày đặt</p>
                                <p className="font-semibold">{booking.item.data.createdAt}</p>
                            </div>
                            <div className="text-right text-sm">
                                <div className="w-[110px] h-[110px] bg-white rounded flex items-center justify-center">
                                    <QRCodeCanvas
                                        value={`/dat-hen/phieu-hen/${booking.item.data.id}.html`}           // Dữ liệu cần mã hóa (text, link, JSON, v.v.)
                                        size={100}             // Kích thước (px)
                                        bgColor="#ffffff"      // Màu nền
                                        fgColor="#000000"      // Màu QR
                                        level="Q"              // Mức độ sửa lỗi (L, M, Q, H)
                                        includeMargin={false}   // Thêm margin xung quanh
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Nội dung */}
                        <div className="p-6 space-y-5">
                            <div className="flex items-center">
                                <i className="fa-2x fa-solid fa-hospital-user text-blue-500 w-10" />
                                <div className="ml-3">
                                    <p className="text-sm text-gray-500">Chuyên khoa</p>
                                    <p className="font-semibold">{booking.item.data.specialty?.name || "-"}</p>
                                </div>
                            </div>
                            <div className="flex items-center">
                                <i className="fa-2x fa-solid fa-user-doctor text-blue-500 w-10" />
                                <div className="ml-3">
                                    <p className="text-sm text-gray-500">Bác sĩ</p>
                                    <p className="font-semibold">{booking.item.data.user ? `${booking.item.data.user.firstName} ${booking.item.data.user.lastName}` : "-"}</p>
                                </div>
                            </div>
                            <div className="flex items-center">
                                <i className="fa-2x fa-solid fa-square-check text-blue-500 w-10" />
                                <div className="ml-3">
                                    <p className="text-sm text-gray-500">Hình thức</p>
                                    <p className="font-semibold">Lấy vé tại quầy</p>
                                </div>
                            </div>
                            <div className="flex items-center">
                                <i className="fa-2x fa-solid fa-clock text-blue-500 w-10" />
                                <div className="ml-3">
                                    <p className="text-sm text-gray-500">Thời gian</p>
                                    <p className="font-semibold">{booking.item.data.date} {booking.item.data.timeFrame?.name}</p>
                                </div>
                            </div>
                            <div className="flex items-center">
                                <i className="fa-2x fa-solid fa-info text-blue-500 w-10" />
                                <div className="ml-3">
                                    <p className="text-sm text-gray-500">Người đặt</p>
                                    <p className="font-semibold">{booking.item.data.fullName}</p>
                                </div>
                            </div>
                            <div className="flex items-center">
                                <i className="fa-2x fa-solid fa-envelope text-blue-500 w-10" />
                                <div className="ml-3">
                                    <p className="text-sm text-gray-500">Email</p>
                                    <p className="font-semibold">{booking.item.data.email || "-"}</p>
                                </div>
                            </div>
                            <div className="flex items-center">
                                <i className="fa-2x fa-solid fa-file-pen text-blue-500 w-10" />
                                <div className="ml-3">
                                    <p className="text-sm text-gray-500">Mô tả triệu chứng</p>
                                    <p className="font-semibold">{booking.item.data.description || "-"}</p>
                                </div>
                            </div>
                        </div>
                        {/* Footer */}
                        <div className="bg-gray-100 py-4 text-center">
                            <button className="px-5 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition" onClick={handleDownload}>
                                Tải xuống hình ảnh
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </Fragment>
    );
};
export default BookingAlert;
